/**
 * Polls a `KothRound` PDA at 1Hz and exposes the decoded state.
 *
 * Reads don't need the wallet, so this uses the read-only program from
 * `client/src/explorer/anchor.ts`. Polling stops once `status === 1 (Settled)`
 * so a settled round doesn't churn RPC indefinitely.
 *
 * Returns `round = null` when the PDA does not exist (round not yet inited)
 * rather than throwing — callers render an "init round" CTA in that case.
 */
import { BN } from "@coral-xyz/anchor";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { useCallback, useEffect, useMemo, useState } from "react";

import { buildReadonlyProgram } from "../../explorer/anchor";
import { deriveRoundPda } from "./anchor";

/** Decoded shape of a `KothRound` account. Mirrors `state.rs::KothRound`. */
export interface KothRoundState {
  roundId: BN;
  /** Undefined on rounds created before the `keeper` field was added to `KothRound`. */
  keeper?: PublicKey;
  createdAt: BN;
  expiresAt: BN;
  initialExpiresAt: BN;
  currentKing: PublicKey;
  touchCount: number;
  potLamports: BN;
  /** 0 = Open, 1 = Settled. */
  status: number;
  /** Undefined on rounds created before `params_snapshot` was added (Phase 1 cutover). */
  paramsSnapshot?: KothRoundParamsSnapshot;
  /** Undefined on rounds created before the `season` field was added (Phase 1 cutover). */
  season?: PublicKey;
  /** Most-recent-unique-toucher ring buffer, position 0 = current king.
   * Undefined on rounds created before the ring buffer existed (Phase 3 cutover). */
  recentTouchers?: PublicKey[];
  bump: number;
}

/** Subset of the decoded `RoundParams` we read in the debug menu. */
export interface KothRoundParamsSnapshot {
  baseFeeLamports: BN;
  stepFeeLamports: BN;
  baseDurationSecs: number;
  extensionSecsPerTouch: number;
  maxTotalExtensionSecs: number;
  cooldownSecs: number;
  protocolSkimBps: number;
  vigBps?: number;
  topNPayout: number;
  keeperBountyLamports: BN;
  cooldownSecsPerWallet: number;
  maxTouchesPerWallet: number;
  endgameThresholdSecs: number;
  extensionSecsEndgame: number;
  dynamicFeeBps?: number;
}

export interface UseKothRoundResult {
  round: KothRoundState | null;
  pda: PublicKey | null;
  loading: boolean;
  error: string | null;
  /** Re-fetch immediately, e.g. right after a successful touch tx. */
  refresh: () => void;
}

const POLL_MS = 1000;

export function useKothRound(roundId: BN | null): UseKothRoundResult {
  const { connection } = useConnection();
  const [round, setRound] = useState<KothRoundState | null>(null);
  const [loading, setLoading] = useState<boolean>(roundId !== null);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const program = useMemo(() => buildReadonlyProgram(connection), [connection]);
  const pda = useMemo(
    () => (roundId ? deriveRoundPda(roundId, program.programId) : null),
    [roundId, program.programId],
  );

  const refresh = useCallback(() => setRefreshTick((n) => n + 1), []);

  useEffect(() => {
    if (!pda) {
      setRound(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const ns = (
      program.account as unknown as Record<
        string,
        { fetch: (pk: PublicKey) => Promise<unknown> }
      >
    )["kothRound"];
    if (!ns || typeof ns.fetch !== "function") {
      setError("IDL is missing the KothRound account schema");
      setLoading(false);
      return;
    }

    const tick = async () => {
      try {
        const acct = (await ns.fetch(pda)) as KothRoundState;
        if (cancelled) return;
        setRound(acct);
        setError(null);
        setLoading(false);
        // Stop polling settled rounds; nothing else will change on-chain.
        if (acct.status === 1) return;
      } catch (e) {
        if (cancelled) return;
        const msg = (e as Error).message ?? String(e);
        // "Account does not exist" is the not-yet-inited case — surface as null,
        // not as an error, so the UI can render the "Start round" CTA.
        if (/does not exist|Account does not exist/i.test(msg)) {
          setRound(null);
          setError(null);
        } else {
          setError(msg);
        }
        setLoading(false);
      }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [program, pda, refreshTick]);

  return { round, pda, loading, error, refresh };
}
